// src/componentes/tienda/tiendaPublica/TarjetaProducto.js
// Tarjeta retail del catálogo público (canon Ripley): foto 1:1, categoría en una
// línea, nombre en dos, precio y abajo las muestras de color con un botón
// redondo de carrito. Filas de alto fijo: las tarjetas quedan alineadas aunque
// cambien los textos. Talla y color se eligen aquí o en el detalle.
import React, { useMemo, useState } from 'react';
import { Heart, Package, ShoppingCart } from 'lucide-react';
import {
  categoriaDe, fotoDeProducto, marcaDe, ETIQUETA_NIVEL_STOCK, nivelStock, soles, stockDeSeleccion, stockPublico,
} from '../../../dominio/logicaTiendaPublica';
import { SelectorColor } from './SelectorColor';

const unicos = (lista) => [...new Set(lista)];

/**
 * Opciones de talla/color derivadas de las variantes con stock. «tallas» y
 * «colores» son las que combinan con lo ya elegido; «…ConStock», las que
 * tienen stock en alguna combinación. Talla y color se eligen en cualquier orden.
 */
export const opcionesVariantes = (producto, talla, color) => {
  const variantes = (producto.variantes || []).filter((v) => Number(v.stock) > 0);
  if (!variantes.length) {
    const tallas = producto.tallas || [], colores = producto.colores || [];
    return { tallas, colores, tallasConStock: tallas, coloresConStock: colores, variantes };
  }
  return {
    tallas: unicos(variantes.filter((v) => !color || v.color === color).map((v) => v.talla)),
    colores: unicos(variantes.filter((v) => !talla || v.talla === talla).map((v) => v.color)),
    tallasConStock: unicos(variantes.map((v) => v.talla)),
    coloresConStock: unicos(variantes.map((v) => v.color)),
    variantes,
  };
};

/** Estado de selección compartido por tarjeta y detalle. */
export const useSeleccionProducto = (producto) => {
  const [talla, setTalla] = useState(producto.tallas?.length === 1 ? producto.tallas[0] : '');
  const [color, setColor] = useState(producto.colores?.length === 1 ? producto.colores[0] : '');
  const opciones = useMemo(() => opcionesVariantes(producto, talla, color), [producto, talla, color]);
  const varianteElegida = opciones.variantes.find((v) => v.talla === talla && v.color === color) || null;
  const usaVariantes = producto.tipoProducto === 'ropa' || opciones.variantes.length > 0;
  const faltaVariante = usaVariantes
    ? !varianteElegida
    : ((producto.tallas?.length > 0 && !talla) || (producto.colores?.length > 0 && !color));
  const stock = usaVariantes ? (varianteElegida ? Number(varianteElegida.stock) : 0) : stockDeSeleccion(producto);
  // Elegir una opción que no combina con la otra suelta la otra (no queda una combinación sin stock).
  const combina = (t, c) => !opciones.variantes.length || opciones.variantes.some((v) => v.talla === t && v.color === c);
  return {
    talla, color, opciones, varianteElegida, faltaVariante, stock,
    elegirTalla: (valor) => { setTalla(valor); if (valor && color && !combina(valor, color)) setColor(''); },
    elegirColor: (valor) => { setColor(valor); if (valor && talla && !combina(talla, valor)) setTalla(''); },
    seleccion: { talla, color, varianteId: varianteElegida?.id || '', skuVariante: varianteElegida?.sku || '' },
  };
};

export const InsigniaStock = ({ nivel }) => (nivel === 'disponible' ? null
  : <span className={`tp-insignia tp-insignia--${nivel}`}>{ETIQUETA_NIVEL_STOCK[nivel]}</span>);

/**
 * Foto completa (sin recortes) sobre una copia desenfocada de sí misma: las
 * fotos con fondo (gym, piso, pared) llenan el marco en vez de quedar como una
 * franja en una caja gris; los recortes sobre blanco se ven igual que siempre.
 * La copia es la misma URL: no descarga nada más.
 */
export const MarcoFoto = ({ url, alt, eager = false, onError }) => <span className="tp-foto-marco">
  <img className="tp-foto-fondo" src={url} alt="" aria-hidden="true" loading={eager ? 'eager' : 'lazy'} decoding="async" referrerPolicy="no-referrer" />
  <img className="tp-foto-principal" src={url} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" referrerPolicy="no-referrer" onError={onError} />
</span>;

/** Foto del producto; en ropa, la del color elegido si el admin subió una. */
export const FotoProducto = ({ producto, tamano = 'tarjeta', color = '' }) => {
  const url = fotoDeProducto(producto, color);
  const [fallida, setFallida] = useState('');
  const conFotoDeColor = Boolean(color) && url === producto.fotosColor?.[color];
  return url && url !== fallida
    ? <MarcoFoto key={url} url={url} alt={conFotoDeColor ? `${producto.nombre}, color ${color}` : producto.nombre}
      eager={tamano === 'detalle'} onError={() => setFallida(url)} />
    : <span className="tp-foto-vacia" role="img" aria-label={`${producto.nombre} sin foto`}><Package size={tamano === 'detalle' ? 64 : 40} aria-hidden="true" /></span>;
};

export const TarjetaProducto = ({ producto, enCarrito = 0, favorito = false, onFavorito, onAgregar, onAbrir }) => {
  const sel = useSeleccionProducto(producto);
  const nivel = nivelStock(stockPublico(producto));
  const agotado = nivel === 'agotado';
  const tituloId = `tp-producto-${producto.id}`;

  return <article className={`tp-tarjeta${agotado ? ' is-agotado' : ''}`} aria-labelledby={tituloId}>
    <div className="tp-tarjeta-media">
      <button type="button" className="tp-tarjeta-foto" onClick={() => onAbrir(producto)}
        aria-label={`Ver detalle de ${producto.nombre}`}>
        <FotoProducto producto={producto} color={sel.color} />
      </button>
      <InsigniaStock nivel={nivel} />
      <button type="button" className="tp-favorito" aria-pressed={favorito}
        aria-label={`${favorito ? 'Quitar' : 'Guardar'} ${producto.nombre} ${favorito ? 'de' : 'en'} favoritos`}
        onClick={() => onFavorito(producto.id)}>
        <Heart size={18} aria-hidden="true" />
      </button>
    </div>
    <div className="tp-tarjeta-info">
      <span className="tp-tarjeta-categoria" title={`${categoriaDe(producto)}${marcaDe(producto) ? ` · ${marcaDe(producto)}` : ''}`}>{categoriaDe(producto)}{marcaDe(producto) ? ` · ${marcaDe(producto)}` : ''}</span>
      <h2 id={tituloId} className="tp-tarjeta-nombre">
        <button type="button" className="tp-enlace-detalle" onClick={() => onAbrir(producto)}>{producto.nombre}</button>
      </h2>
      <p className="tp-precio">{soles(producto.precio)}</p>
      {producto.tallas?.length > 0 && <div className="tp-tarjeta-opciones">
        <label><span>Talla</span>
          <select aria-label={`Talla de ${producto.nombre}`} value={sel.talla} onChange={(e) => sel.elegirTalla(e.target.value)} disabled={agotado}>
            <option value="">Elige</option>
            {sel.opciones.tallasConStock.map((t) => <option key={t} value={t}>
              {sel.opciones.tallas.includes(t) ? t : `${t} (otro color)`}</option>)}
          </select></label>
      </div>}
      <div className="tp-tarjeta-pie">
        {producto.colores?.length > 0 ? <SelectorColor compacto max={4} etiqueta={`Color de ${producto.nombre}`}
          colores={producto.colores} tonos={producto.tonosColor} disponibles={sel.opciones.colores} conStock={sel.opciones.coloresConStock}
          valor={sel.color} onElegir={sel.elegirColor} deshabilitado={agotado} onMas={() => onAbrir(producto)} /> : <span />}
        {/* Botón redondo de carrito. Sin talla o color elegidos, abre el detalle para elegirlos. */}
        <button type="button" className="tp-agregar-icono" disabled={agotado || (!sel.faltaVariante && sel.stock <= 0)}
          aria-label={sel.faltaVariante && !agotado ? `Elegir talla y color de ${producto.nombre}` : `Agregar ${producto.nombre} al carrito`}
          title={agotado ? 'Agotado' : sel.faltaVariante ? 'Elige talla y color' : 'Agregar al carrito'}
          onClick={() => (sel.faltaVariante ? onAbrir(producto) : onAgregar(producto, 1, sel.seleccion))}>
          <ShoppingCart size={18} aria-hidden="true" />
          {enCarrito > 0 && <span className="tp-agregar-cuenta" aria-hidden="true">{enCarrito}</span>}
        </button>
      </div>
      {enCarrito > 0 && <span className="tp-solo-lector" role="status">{enCarrito} en tu carrito</span>}
    </div>
  </article>;
};
